import React, { useEffect, useState } from "react";
import { Button, PlusIcon, TrashIcon, EditIcon } from "evergreen-ui";
import { Card, Table, Input, Form, Select, Modal, message, Tag, Collapse } from "antd";
import FireFetch from "../../../Firebase/FireFetch";
import { TableExport } from "tableexport";
import { useListVals } from "../../../Firebase/databaseHooks";
import Firebase from "../../../Firebase/Firebase";
import { getDatabase, ref, update } from "firebase/database";
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import TableActionMenu from "../../../Common/TableActionMenu";

const { Search } = Input;
const { Panel } = Collapse;
var db = getDatabase(Firebase);

const SubCategoriesLayout = (props) => {
	const canCreate = props.canCreate !== false;
	const canUpdate = props.canUpdate !== false;
	const canDelete = props.canDelete !== false;
	const [form] = Form.useForm();
	const columns = [
		{
			title: 'Reference Name',
			dataIndex: 'categoryName',
			key: 'categoryName',
			width: '25%',
			sorter: (a, b) => a.categoryName?.localeCompare(b.categoryName),
		},
		{
			title: 'Category',
			dataIndex: 'category',
			key: 'category',
			width: '25%',
			sorter: (a, b) => a.category?.localeCompare(b.category),
		},
		{
			title: 'Translations',
			dataIndex: 'subcategoryTranslations',
			key: 'translations',
			render: (translations) => (
				<Collapse ghost size="small">
					<Panel header={`${Object.keys(translations || {}).length} translations`} key="1">
						<div style={{ maxHeight: '200px', overflowY: 'auto' }}>
							{translations && Object.entries(translations).map(([langId, text]) => (
								<Tag key={langId} color="blue" style={{ margin: '4px' }}>
									{text}
								</Tag>
							))}
						</div>
					</Panel>
				</Collapse>
			),
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			width: '20%',
			render: (_, record) => (
				<TableActionMenu
					items={[
						canUpdate && {
							key: "edit",
							label: "Edit",
							onClick: () => handleEditClick(record),
						},
						canDelete && {
							key: "delete",
							label: "Delete",
							danger: true,
							onClick: () => showDeleteConfirm(record.id),
						},
					]}
				/>
			),
		}
	];

	const [dataArray, setDataArray] = useState([]);
	const [categoryList, setCategoryList] = useState([]);
	const [arrayIn, loading] = useListVals(ref(db, "talmidsdb/subCategories"));
	const [categories] = useListVals(ref(db, "talmidsdb/categories"));
	const [languages] = useListVals(ref(db, "talmidsdb/languages"));
	const [isEditModalVisible, setIsEditModalVisible] = useState(false);
	const [currentRecord, setCurrentRecord] = useState(null);

	useEffect(() => {
		if (arrayIn && categories) {
			const tempArray = arrayIn.map((item) => ({
				key: item.id,
				id: item.id,
				categoryName: item.categoryName,
				categoryID: item.categoryID,
				category: categories.find(x => x.id === item.categoryID)?.name,
				subcategoryTranslations: item.subcategoryTranslations || {}
			}));
			setCategoryList(tempArray);
			setDataArray(tempArray);
		}
	}, [arrayIn, categories]);

	const handleSearch = searchText => {
		searchText = searchText.toLowerCase();
		const filteredEvents = dataArray.filter(({ categoryName, category }) => {
			categoryName = categoryName && categoryName.toLowerCase();
			category = category && category.toLowerCase();
			return (categoryName && categoryName.includes(searchText)) ||
				(category && category.includes(searchText));
		});
		setCategoryList(filteredEvents);
	};

	const handleEditClick = (record) => {
		if (!canUpdate) {
			message.error("You do not have permission to update sub-categories.");
			return;
		}

		setCurrentRecord(record);
		form.setFieldsValue({
			categoryName: record.categoryName,
			categoryID: record.categoryID,
			translations: translationsToArray(record.subcategoryTranslations)
		});
		setIsEditModalVisible(true);
	};

	const handleEditSubmit = async () => {
		if (!canUpdate) {
			message.error("You do not have permission to update sub-categories.");
			return;
		}

		try {
			const values = await form.validateFields();
			const updatedTranslations = {};

			if (values.translations) {
				values.translations.forEach(translation => {
					if (translation.languageId && translation.text) {
						updatedTranslations[translation.languageId] = translation.text;
					}
				});
			}

			await update(ref(db, `talmidsdb/subCategories/${currentRecord.id}`), {
				categoryName: values.categoryName,
				categoryID: values.categoryID,
				subcategoryTranslations: updatedTranslations
			});
			message.success('Sub-category updated successfully');
			setIsEditModalVisible(false);
		} catch (error) {
			message.error('Failed to update sub-category: ' + error.message);
		}
	};

	const translationsToArray = (translations) => {
		if (!translations) return [];
		return Object.entries(translations).map(([languageId, text]) => ({
			languageId,
			text
		}));
	};

	const showDeleteConfirm = (id) => {
		if (!canDelete) {
			message.error("You do not have permission to delete sub-categories.");
			return;
		}

		Modal.confirm({
			title: 'Delete Sub-Category',
			content: 'Are you sure you want to delete this sub-category?',
			okText: 'Yes',
			okType: 'danger',
			cancelText: 'No',
			onOk() {
				props.messageBack("subcategories", "delete", id);
			},
		});
	};

	return (
		<div className="font-Nunito">
			<div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
				{canCreate && (
					<Button
						appearance="primary"
						onClick={() => props.messageBack("subcategories", "create")}
						iconBefore={<PlusIcon />}
					>
						New SubCategory
					</Button>
				)}
				<Button
					appearance="primary"
					onClick={() => {
						FireFetch.exportXL(TableExport(document.getElementById("dataTable"), {
							filename: "SubCategories",
							exportButtons: false,
							sheetname: "SubCategories",
						}), categoryList);
					}}
				>
					Print
				</Button>
				<Search
					placeholder="Filter subcategories"
					onChange={e => handleSearch(e.target.value)}
					style={{ width: 250 }}
				/>
			</div>

			<Card>
				<Table
					id="dataTable"
					loading={loading}
					columns={columns}
					dataSource={categoryList}
					pagination={{
						defaultPageSize: 100,
						showSizeChanger: true,
						pageSizeOptions: ['50', '100', '200']
					}}
					scroll={{ x: true }}
				/>
			</Card>

			<Modal
				title="Edit Sub-Category"
				visible={isEditModalVisible}
				onOk={handleEditSubmit}
				onCancel={() => setIsEditModalVisible(false)}
				okText="Save Changes"
				okButtonProps={{ disabled: !canUpdate }}
				width={800}
			>
				<Form form={form} layout="vertical">
					<Form.Item
						name="categoryName"
						label="Reference Name"
						rules={[{ required: true, message: 'Please input reference name!' }]}
					>
						<Input placeholder="Enter reference name" />
					</Form.Item>

					<Form.Item
						name="categoryID"
						label="Parent Category"
						rules={[{ required: true, message: 'Please select a category!' }]}
					>
						<Select placeholder="Select category">
							{categories?.map(category => (
								<Select.Option key={category.id} value={category.id}>
									{category.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.List name="translations">
						{(fields, { add, remove }) => (
							<div>

								{fields.map(({ key, name, ...restField }) => (
									<div key={key} style={{
										display: 'flex',
										gap: '16px',
										marginBottom: '16px',
										alignItems: 'flex-end'
									}}>
										<Form.Item
											{...restField}
											name={[name, 'languageId']}
											label="Language"
											rules={[{ required: true }]}
											style={{ flex: 1 }}
										>
											<Select placeholder="Select language">
												{languages?.map(lang => (
													<Select.Option key={lang.id} value={lang.id}>
														{lang.languageName}
													</Select.Option>
												))}
											</Select>
										</Form.Item>

										<Form.Item
											{...restField}
											name={[name, 'text']}
											label="Translation"
											rules={[{ required: true }]}
											style={{ flex: 2 }}
										>
											<Input placeholder="Enter translation" />
										</Form.Item>

										<MinusCircleOutlined
											onClick={() => remove(name)}
											style={{ color: '#ff4d4f', marginBottom: '32px' }}
										/>
									</div>
								))}

								<Form.Item>
									<Button
										type="dashed"
										onClick={() => add()}
										block
										icon={<PlusOutlined />}
									>
										Add Translation
									</Button>
								</Form.Item>
							</div>
						)}
					</Form.List>
				</Form>
			</Modal>
		</div>
	);
};

export default SubCategoriesLayout;
